import type {
  AssignmentPointsItem,
  AssignmentPointsNoteDoc,
  AssignmentStudentStatus,
} from '../types/assignmentPointsNote';
import type { ClassMemberGradeLayout } from './classMembersStorage';
import { ANNUAL_WEEKDAY_KEYS } from './annualTimetable';
import { dateToWeekdayKey } from './dashboardDateContext';
import { resolveAnnualProgressKeysForDate } from './dashboardTermResolve';
import { parseISODate } from './date';
import { recordMatchesClassRecordGroupFilter } from './classMemberRecordGroupUtils';

export const ASSIGNMENT_MEMO_RECORD_KIND = 'assignment_memo' as const;
export type AssignmentMemoRecordKind = typeof ASSIGNMENT_MEMO_RECORD_KIND;

export type AssignmentMemoMemberRef = {
  id: string;
  name: string;
  orderIndex: number;
  groupId: string;
};

export type AssignmentMemoRowInput = {
  key: string;
  termNumber: 1 | 2;
  sortKey: string;
  hasProgramSchedule: boolean;
  dateLabel: string;
  weekdayLabel: string;
  periodLabel: string;
  programLabel: string;
  programDotKind: 'fixed' | 'project' | 'track' | 'none';
  timetableText: string;
  unitLabel: string;
  learningLabel: string;
  recordKind: AssignmentMemoRecordKind;
  kindLabel: string;
  note: string;
  recordMemberGroupId: string | null;
  /** 과제 item id — 수업/평가 연결·중복 생략용 */
  assignmentItemId: string;
};

export type AssignmentMemoLinkCandidate = {
  itemId: string;
  kind: 'general' | 'project';
  title: string;
  dueDate: string;
  categoryLabel: string;
  categoryColor: string;
  basePoints: number;
  memoLineCount: number;
  coverImageId?: string;
  /** 선택 그룹 기준 `#이름 메모` 본문 */
  memoNote: string;
  /** 피커에서 선택한 멤버 그룹(학급). null이면 전체 */
  groupId: string | null;
  groupLabel: string;
};

const WEEKDAY_KO = ['일', '월', '화', '수', '목', '금', '토'] as const;

function shortDateLabel(iso: string): string {
  const d = parseISODate(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getMonth() + 1}월${d.getDate()}일`;
}

function categoryMeta(
  doc: AssignmentPointsNoteDoc,
  categoryId: string | undefined
): { name: string; color: string } {
  const id = (categoryId ?? '').trim();
  if (!id) return { name: '기본', color: 'rgba(162, 162, 162, 1)' };
  const hit = doc.settings.categories.find((c) => c.id === id);
  return {
    name: (hit?.name ?? '').trim() || '기본',
    color: (hit?.color ?? '').trim() || 'rgba(162, 162, 162, 1)',
  };
}

function memberOrderIndex(
  membersById: Map<string, AssignmentMemoMemberRef>
): (a: string, b: string) => number {
  return (a, b) => {
    const ma = membersById.get(a);
    const mb = membersById.get(b);
    const ga = (ma?.groupId ?? '').localeCompare(mb?.groupId ?? '', 'ko');
    if (ga !== 0) return ga;
    const oa = ma?.orderIndex ?? 9999;
    const ob = mb?.orderIndex ?? 9999;
    if (oa !== ob) return oa - ob;
    return (ma?.name ?? '').localeCompare(mb?.name ?? '', 'ko');
  };
}

function buildTaggedMemoLines(
  statuses: AssignmentStudentStatus[],
  membersById: Map<string, AssignmentMemoMemberRef>,
  allowedMemberIds: Set<string> | null,
  sortMemberIds: (a: string, b: string) => number
): string[] {
  const withMemo = statuses
    .filter((s) => {
      if (!(s.memo ?? '').trim()) return false;
      if (allowedMemberIds && !allowedMemberIds.has(s.studentId)) return false;
      return membersById.has(s.studentId);
    })
    .slice();
  withMemo.sort((a, b) => sortMemberIds(a.studentId, b.studentId));
  const lines: string[] = [];
  for (const s of withMemo) {
    const name = (membersById.get(s.studentId)?.name ?? '').trim().replace(/\s+/g, '');
    if (!name) continue;
    const memo = (s.memo ?? '').replace(/\s+/g, ' ').trim();
    if (!memo) continue;
    lines.push(`#${name} ${memo}`);
  }
  return lines;
}

function inferStoredGroupId(
  lines: string[],
  membersById: Map<string, AssignmentMemoMemberRef>
): string | null {
  const groupIds = new Set<string>();
  for (const line of lines) {
    const m = line.match(/^#([^\s#]+)/);
    if (!m) continue;
    const name = m[1];
    for (const mem of Array.from(membersById.values())) {
      if ((mem.name ?? '').trim().replace(/\s+/g, '') === name && mem.groupId) {
        groupIds.add(mem.groupId);
      }
    }
  }
  return groupIds.size === 1 ? Array.from(groupIds)[0]! : null;
}

function statusesForAssignmentItem(item: AssignmentPointsItem): AssignmentStudentStatus[] {
  if (item.kind === 'general') return item.studentStatuses ?? [];
  if (item.kind !== 'project') return [];
  const memosByStudent = new Map<string, string[]>();
  const subs = [...(item.subTasks ?? [])].sort((a, b) => a.order - b.order);
  for (const sub of subs) {
    for (const mid of item.assignedMemberIds ?? []) {
      const st = item.studentStatusesBySubTask?.[`${sub.id}:${mid}`];
      const memo = (st?.memo ?? '').trim();
      if (!memo) continue;
      const list = memosByStudent.get(mid) ?? [];
      list.push(memo);
      memosByStudent.set(mid, list);
    }
  }
  return Array.from(memosByStudent.entries()).map(([studentId, memos]) => ({
    studentId,
    status: 'pending' as const,
    earnedPoints: 0,
    memo: memos.join(' '),
  }));
}

/** 일반·프로젝트 중 선택 학급에 배정되고, 그 학급 멤버 메모가 있는 과제 (가져오기 피커용) */
export function listAssignmentMemoLinkCandidates(
  doc: AssignmentPointsNoteDoc | null,
  membersById: Map<string, AssignmentMemoMemberRef>,
  opts?: {
    allowedMemberIds?: Set<string> | null;
    groupId?: string | null;
    groupLabel?: string;
  }
): AssignmentMemoLinkCandidate[] {
  if (!doc) return [];
  const sortMembers = memberOrderIndex(membersById);
  const allowed = opts?.allowedMemberIds ?? null;
  const groupId = (opts?.groupId ?? '').trim() || null;
  const groupLabel = (opts?.groupLabel ?? '').trim();
  const out: AssignmentMemoLinkCandidate[] = [];
  for (const item of doc.items) {
    if (item.kind !== 'general' && item.kind !== 'project') continue;
    const assignedIds = item.assignedMemberIds ?? [];
    if (allowed) {
      const assignedToGroup = assignedIds.some((id) => allowed.has(id));
      if (!assignedToGroup) continue;
    }
    const lines = buildTaggedMemoLines(
      statusesForAssignmentItem(item),
      membersById,
      allowed,
      sortMembers
    );
    if (lines.length === 0) continue;
    const dueDate = (item.dueDate ?? '').trim();
    const category =
      item.kind === 'general'
        ? categoryMeta(doc, item.categoryId)
        : { name: '프로젝트', color: (item.color ?? '').trim() || '#FC5555' };
    const basePoints =
      item.kind === 'general'
        ? Math.max(0, Number(item.basePoints) || 0)
        : (item.subTasks ?? []).reduce((s, t) => s + (Number(t.points) || 0), 0);
    out.push({
      itemId: item.id,
      kind: item.kind,
      title: (item.title ?? '').trim() || '제목 없음',
      dueDate,
      categoryLabel: category.name,
      categoryColor: category.color,
      basePoints,
      memoLineCount: lines.length,
      coverImageId: (item.coverImageId ?? '').trim() || undefined,
      memoNote: lines.join('\n'),
      groupId,
      groupLabel,
    });
  }
  out.sort((a, b) => {
    const d = (b.dueDate || '').localeCompare(a.dueDate || '');
    if (d !== 0) return d;
    return a.title.localeCompare(b.title, 'ko');
  });
  return out;
}

/** 연결 조회: 과제 item → `#이름 메모` 본문 */
export function resolveLinkedAssignmentMemoNote(params: {
  doc: AssignmentPointsNoteDoc | null;
  itemId: string;
  membersById: Map<string, AssignmentMemoMemberRef>;
  allowedMemberIds?: Set<string> | null;
}): string {
  const id = (params.itemId ?? '').trim();
  if (!params.doc || !id) return '';
  const item = params.doc.items.find((it) => it.id === id);
  if (!item || (item.kind !== 'general' && item.kind !== 'project')) return '';
  const lines = buildTaggedMemoLines(
    statusesForAssignmentItem(item),
    params.membersById,
    params.allowedMemberIds ?? null,
    memberOrderIndex(params.membersById)
  );
  return lines.join('\n');
}

/**
 * 과제 체크 메모 → 기록 모아보기 행.
 * 일반·프로젝트만. 날짜=마감일. 구분=과제메모. 단원=카테고리. 학습내용=과제 제목.
 */
export function buildAssignmentMemoRows(params: {
  doc: AssignmentPointsNoteDoc | null;
  layout: ClassMemberGradeLayout;
  membersById: Map<string, AssignmentMemoMemberRef>;
  workspaceId: string;
  academicYear: number;
  selectedRecordGroupFilterId?: string;
  allRecordGroupsFilterId?: string;
  generalRecordGroupsFilterId?: string;
  /** 수업/평가에 이미 연결된 과제 — 단독 과제메모 뷰가 아니면 행 생략 */
  omitLinkedAssignmentItemIds?: Set<string> | null;
}): AssignmentMemoRowInput[] {
  const doc = params.doc;
  if (!doc || !params.workspaceId) return [];

  const allGroupsId = params.allRecordGroupsFilterId ?? '__all_record_groups__';
  const generalGroupsId = params.generalRecordGroupsFilterId ?? '__general_record_groups__';
  const groupFilter = params.selectedRecordGroupFilterId ?? allGroupsId;
  const sortMembers = memberOrderIndex(params.membersById);
  const omitLinked = params.omitLinkedAssignmentItemIds ?? null;

  let allowedMemberIds: Set<string> | null = null;
  if (groupFilter !== allGroupsId && groupFilter !== generalGroupsId) {
    allowedMemberIds = new Set(
      (params.layout.membersByGroupId[groupFilter] ?? []).map((m) => m.id)
    );
  }

  const out: AssignmentMemoRowInput[] = [];

  const pushItemRow = (
    item: AssignmentPointsItem,
    statuses: AssignmentStudentStatus[],
    rowKeySuffix: string,
    unitLabel: string
  ) => {
    if (omitLinked?.has(item.id)) return;

    const lines = buildTaggedMemoLines(
      statuses,
      params.membersById,
      allowedMemberIds,
      sortMembers
    );
    if (lines.length === 0) return;

    const note = lines.join('\n');
    const storedGroupId = inferStoredGroupId(lines, params.membersById);
    if (
      !recordMatchesClassRecordGroupFilter({
        selectedFilterId: groupFilter,
        allFilterId: allGroupsId,
        generalFilterId: generalGroupsId,
        storedGroupId,
        note,
        layout: params.layout,
      })
    ) {
      return;
    }

    const dueIso = (item.dueDate ?? '').trim();
    if (!dueIso) return;
    const date = parseISODate(dueIso);
    if (Number.isNaN(date.getTime())) return;

    const termResolved = resolveAnnualProgressKeysForDate(
      params.workspaceId,
      dueIso,
      params.academicYear
    );
    if (termResolved.academicYear !== params.academicYear) return;
    const termNumber = termResolved.termNumber;

    const weekdayKey = dateToWeekdayKey(date);
    const weekdayLabel =
      weekdayKey != null
        ? (ANNUAL_WEEKDAY_KEYS.find((w) => w.key === weekdayKey)?.label ?? weekdayKey)
        : WEEKDAY_KO[date.getDay()] ?? '—';
    const dateLabel = shortDateLabel(dueIso);
    const title = (item.title ?? '').trim() || '제목 없음';

    out.push({
      key: `assignment-memo:${item.id}:${rowKeySuffix}`,
      termNumber,
      sortKey: `${termNumber}|${dueIso}|990|assignment|${item.id}|${rowKeySuffix}`,
      hasProgramSchedule: true,
      dateLabel,
      weekdayLabel,
      periodLabel: '—',
      programLabel: '—',
      programDotKind: 'none',
      timetableText: '',
      unitLabel,
      learningLabel: title,
      recordKind: ASSIGNMENT_MEMO_RECORD_KIND,
      kindLabel: '과제메모',
      note,
      recordMemberGroupId: storedGroupId,
      assignmentItemId: item.id,
    });
  };

  for (const item of doc.items) {
    if (item.kind === 'general') {
      pushItemRow(
        item,
        statusesForAssignmentItem(item),
        'general',
        categoryMeta(doc, item.categoryId).name
      );
      continue;
    }
    if (item.kind === 'project') {
      pushItemRow(item, statusesForAssignmentItem(item), 'project', '');
    }
  }

  return out;
}
